<script setup>
import axios from 'axios';
import { BButton, BForm, BFormCheckbox, BFormGroup, BLink, BModal } from 'bootstrap-vue-next';
import { onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';

import HackathonDisqualificationSection from './user-edit-page/HackathonDisqualificationSection.vue';

const route = useRoute();
const router = useRouter();

const busy = ref(true);
const error = ref(false);
const user = ref({});
const dirty = ref(false);
const showDeleteConfirm = ref(false);
const showSuccessModal = ref(false);
let originalCommunityMemberOverrideValue;
const fetchData = async () => {
  const { userId } = route.params;

  try {
    const response = await axios.get(`/api/v1/users/${userId}/edit`);
    user.value = response.data;
    originalCommunityMemberOverrideValue = user.value.community_member_override;
    document.title = `Edit ${user.value.username}`;
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
  }
};

const save = (event) => {
  if (!dirty.value) {
    return;
  }

  event.preventDefault();
  axios.put(`/api/v1/users/${user.value.id}`, {
    community_member_override: user.value.community_member_override,
  });
};

const update = () => {
  dirty.value = originalCommunityMemberOverrideValue != user.value.community_member_override;
};

const clear = () => {
  user.value.community_member_override = null;
  dirty.value = originalCommunityMemberOverrideValue != user.value.community_member_override;
};

const onDisqualificationChanged = (disqualification) => {
  user.value.hackathon.disqualification = disqualification;
};

const deleteUser = async () => {
  if (!showDeleteConfirm.value) {
    showDeleteConfirm.value = true;

    return;
  }

  await axios.delete(`/api/v1/users/${user.value.id}`);
  showDeleteConfirm.value = false;
  showSuccessModal.value = true;
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
};

const handleSuccessModalClose = () => {
  showSuccessModal.value = false;
  router.push('/');
};

onMounted(fetchData);
</script>

<template>
  <ErrorState v-if="error" />
  <template v-else>
    <BForm @submit="save">
      <h1>{{ user.username }}</h1>
      <BFormGroup label="Community member (calculated)">
        <BFormCheckbox
          v-model="user.community_member"
          disabled
        />
      </BFormGroup>
      <BFormGroup label="Community member (override)">
        <div class="d-flex">
          <BFormCheckbox
            v-model="user.community_member_override"
            :unchecked-value="false"
            :indeterminate="user?.community_member_override == null"
            data-testid="community-member-override-checkbox"
            @update:model-value="update"
          />
          <BLink
            v-if="user?.community_member_override != null"
            type="button"
            variant="outline-secondary"
            class="ms-2"
            @click="clear"
          >
            Clear
          </BLink>
        </div>
      </BFormGroup>
      <BButton
        type="submit"
        variant="primary"
        class="mt-3"
      >
        Save
      </BButton>
    </BForm>

    <HackathonDisqualificationSection
      v-if="user.hackathon"
      :username="user.username"
      :hackathon="user.hackathon"
      @changed="onDisqualificationChanged"
    />

    <div class="mt-5 pt-4 border-top">
      <p>Delete this user permanently.</p>
      <div class="d-flex gap-2">
        <BButton
          type="button"
          variant="danger"
          @click="deleteUser"
        >
          {{ showDeleteConfirm ? 'Confirm Delete' : 'Delete User' }}
        </BButton>
        <BButton
          v-if="showDeleteConfirm"
          type="button"
          variant="outline-secondary"
          @click="cancelDelete"
        >
          Cancel
        </BButton>
      </div>
      <small
        v-if="showDeleteConfirm"
        class="text-danger d-block mt-2"
      >
        Warning: This action cannot be undone
      </small>
    </div>

    <BModal
      v-model="showSuccessModal"
      title="Success"
      ok-only
      @ok="handleSuccessModalClose"
      @hide="handleSuccessModalClose"
    >
      User deleted successfully
    </BModal>
  </template>
</template>
